import {createDivIn, createHeaderIn, createParagraphIn, createImgIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils'
import {TranslationObject} from '@colonist/i18n'
import {ViewPopupOptions} from './ViewPopupOptions'
import {KeyBindController, ShortcutLocation} from '../KeyBindController'
import {UIPopupManager} from './UIPopupManager'
import {LobbyImage} from '@colonist/types'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {hideTooltip} from '../ui_utils/TooltipUtils.ts'
import {AudioManager, AudioType} from '@colonist/audio'

export class ViewPopup {
    protected readonly container: HTMLDivElement
    readonly contentContainer: HTMLDivElement
    readonly header: HTMLDivElement
    readonly body: HTMLDivElement
    protected readonly footer: HTMLDivElement

    preventClicksOutsideOfPopupFromClosingPopup: boolean

    readonly checkButton: HTMLDivElement
    readonly xButton: HTMLDivElement
    removeAction: () => void = () => undefined

    readonly options?: ViewPopupOptions | undefined

    constructor(title: TranslationObject, body: TranslationObject | undefined, checkAction: () => void, xAction: () => void, canCancel: boolean, options?: ViewPopupOptions) {
        this.container = createDivIn(document.body, 'popup')
        this.contentContainer = createDivIn(this.container, 'popup-content')
        this.header = createDivIn(this.contentContainer, 'popup-header')
        this.body = createDivIn(this.contentContainer, 'popup-body')
        this.footer = createDivIn(this.contentContainer, 'popup-footer')
        this.options = options

        this.xButton = createDivIn(this.footer, 'btn_general btn_general_secondary btn_general_x')
        setElementOnClick(this.xButton, () => {
            AudioManager.play(AudioType.Click)
            xAction()
            this.removePopup()
        })

        this.checkButton = createDivIn(this.footer, 'btn_general btn_general_check')
        setElementOnClick(this.checkButton, () => {
            AudioManager.play(AudioType.Click)
            checkAction()
            this.removePopup()
        })

        this.preventClicksOutsideOfPopupFromClosingPopup = false
        if(canCancel) this.setCancel()

        createHeaderIn(this.header, title, '', '', 'H2')
        if(body != undefined) createParagraphIn(this.body, body, '')

        this.applyOptions(options)
        UIPopupManager.trackPopup(this)
    }

    makeLarge() {
        this.body.className = 'popup-body-large'
        this.contentContainer.className = 'popup-content-large'
    }

    changeCheckButtonText(text: TranslationObject) {
        setElementInnerHTML(this.checkButton, text)
        this.checkButton.className = 'btn_general'
    }

    disableCheckButton() {
        this.checkButton.style.opacity = '0.5'
        this.checkButton.style.pointerEvents = 'none'
    }

    enableCheckButton() {
        this.checkButton.style.opacity = '1.0'
        this.checkButton.style.pointerEvents = 'auto'
    }

    changeXButtonText(text: TranslationObject) {
        setElementInnerHTML(this.xButton, text)
        this.xButton.classList.remove('btn_general_x')
    }

    isRemoved(): boolean {
        return this.container.style.display == 'none'
    }

    show() {
        this.container.style.display = 'block'
        hideTooltip()
    }

    removePopup() {
        this.removeAction()
        this.container.style.display = 'none'
        if(this.container.parentElement != undefined) this.container.parentElement.removeChild(this.container)
        UIPopupManager.untrackPopup(this)
    }

    isPopupRemoved(): boolean {
        return !UIPopupManager.isPopupTracked(this)
    }

    private setCancel() {
        this.container.addEventListener('touchend', (touchEndEvent: TouchEvent) => {
            if(this.preventClicksOutsideOfPopupFromClosingPopup) return

            const touchEndedOutside = touchEndEvent.target == this.container
            if(!touchEndedOutside) return

            touchEndEvent.preventDefault()
            this.removePopup()
        })

        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) return

        let clickStartedOutside = false

        window.addEventListener('pointerdown', (pointerDownEvent: MouseEvent) => {
            if(this.preventClicksOutsideOfPopupFromClosingPopup) return

            clickStartedOutside = pointerDownEvent.target == this.container
        })

        window.addEventListener('pointerup', (pointerUpEvent) => {
            if(!clickStartedOutside) return

            const clickEndedOutside = pointerUpEvent.target == this.container
            if(!clickEndedOutside) return

            this.removePopup()
        })

        this.setEscapeKeyBind()
    }

    removeCheckButton() {
        this.checkButton.remove()
    }

    removeXButton() {
        this.xButton.remove()
    }

    setEscapeKeyBind() {
        if(this.preventClicksOutsideOfPopupFromClosingPopup) return
        KeyBindController.bindKey('Escape', () => this.removePopup(), () => !this.isRemoved(), ShortcutLocation.General)
    }

    removeFooter() {
        this.footer.style.display = 'none'
        this.body.style.border = '0px'
    }

    applyOptions(options?: ViewPopupOptions) {
        if(options == undefined) return

        if(options.showXButton === false) this.removeXButton()
        if(options.showCheckButton === false) this.removeCheckButton()
        if(options.showXonHeader === true) this.addXOnHeader()
        if(options.backButton?.showBackButton === true) this.addBackButton()

        if(options.xButtonCustomContent != undefined) {
            this.xButton.classList.remove('btn_general_x')
            setElementInnerHTML(this.xButton, options.xButtonCustomContent)
        }

        if(options.checkButtonCustomContent != undefined) {
            this.checkButton.classList.remove('btn_general_check')
            setElementInnerHTML(this.checkButton, options.checkButtonCustomContent)
        }
    }

    private addXOnHeader() {
        const cancelElement = createImgIn(this.header, LobbyImage.XMark, 'popup-close')
        setElementOnClick(cancelElement, () => {
            AudioManager.play(AudioType.Click)
            this.removePopup()
        })
    }

    private addBackButton() {
        const backElement = createImgIn(this.header, LobbyImage.LeftArrow, 'popup-back')
        setElementOnClick(backElement, () => {
            AudioManager.play(AudioType.Click)
            this.options?.backButton?.backButtonAction?.()
        })
    }

    addBodyContent(content: TranslationObject) {
        createParagraphIn(this.body, content, '')
    }

    protected setFocusStyles(inputElement: HTMLInputElement | HTMLTextAreaElement, shouldScrollIntoView: boolean = false): void {
        if(!UIUtilsEjsVariables.isUserOnDiscordMobile) return

        inputElement.addEventListener('focus', () => {
            this.container.classList.add('popup-focus-mobile')
            if(!shouldScrollIntoView) return
            setTimeout(() => {
                inputElement.scrollIntoView({behavior: 'smooth', block: 'center'})
            }, 100)
        })

        inputElement.addEventListener('focusout', () => {
            this.container.classList.remove('popup-focus-mobile')
        })
    }
}
